import { useMemo } from 'react';
import { SectionCard, fmtTRY } from '../yeni-design/primitives';
import { subeKategoriMatrisi } from './analizHesaplari';
import { Bilgi } from './Bilgi';
import { BlokIskeleti } from './BlokIskeleti';
import type { SubeKategoriSatiri } from './detay/SubeDetay';
import type { Donem } from './donem';
import type { SaKaynak } from './ekranKaynaklari';
import { useSaSorgu } from './useSatisVerisi';

interface Props {
  kaynak: SaKaynak | undefined;
  donem: Donem;
  sube?: string | null;
  kanal?: string | null;
  onSubeSec?: (sube: string) => void;
  onKategoriSec?: (kategori: string) => void;
}

/**
 * Kanal sekmesi : kategori × sube tablosu (990017). Rapor @LOKASYON/@KANAL bildirmez: sorgu yalniz tarihle, sube
 * istemcide suzulur, kanala suzulemez (baslikta ⓘ). Anahtar sube detay penceresiyle ortak; o pencere ek istek atmaz.
 */
export function SubeKategoriBlok({ kaynak, donem, sube = null, kanal = null, onSubeSec, onKategoriSec }: Props) {
  const q = useSaSorgu<SubeKategoriSatiri>(kaynak, { donem, sube: null, kanal: null });
  const m = useMemo(() => subeKategoriMatrisi(q.data?.data ?? [], sube), [q.data, sube]);

  if (!kaynak) return null;
  if (q.isLoading) return <BlokIskeleti testid="blok-sube-kategori-iskelet" baslik="Şube × kategori" yukseklik={320} satir={8} />;
  if (m.subeler.length === 0 || m.kategoriler.length === 0) return null;

  const zemin = (ciro: number) =>
    `color-mix(in srgb, var(--color-primary) ${m.enBuyuk > 0 ? Math.round((Math.max(ciro, 0) / m.enBuyuk) * 28) : 0}%, transparent)`;

  return (
    <div data-testid="blok-sube-kategori">
      <SectionCard
        title={
          <span className="inline-flex items-center gap-1.5">
            Şube × kategori
            {kanal !== null && <Bilgi testid="bilgi-sube-kategori-kanal" metin="kanal süzgeci uygulanmaz" />}
          </span>
        }
        subtitle="Renk koyuluğu ciroyu gösterir"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-[12px] text-ink-500 border-b border-border">
                <th className="text-left font-medium py-2 pr-3">Kategori</th>
                {m.subeler.map((s) => (
                  <th key={s} className="text-right font-medium py-2 px-3 whitespace-nowrap">
                    {onSubeSec ? (
                      <button type="button" onClick={() => onSubeSec(s)} className="focus-ring hover:underline underline-offset-2">
                        {s}
                      </button>
                    ) : (
                      s
                    )}
                  </th>
                ))}
                {m.subeler.length > 1 && <th className="text-right font-medium py-2 pl-3">Toplam</th>}
              </tr>
            </thead>
            <tbody>
              {m.kategoriler.map((k) => (
                <tr key={k.ad} data-testid={`sube-kategori-${k.ad}`} className="border-b border-border last:border-b-0">
                  <td className="py-2 pr-3 font-medium text-ink-900 whitespace-nowrap">
                    {onKategoriSec ? (
                      <button type="button" onClick={() => onKategoriSec(k.ad)} className="focus-ring hover:underline underline-offset-2">
                        {k.ad}
                      </button>
                    ) : (
                      k.ad
                    )}
                  </td>
                  {m.subeler.map((s) => {
                    const c = k.hucreler[s] ?? 0;
                    return (
                      <td key={s} className="py-2 px-3 text-right tnum text-ink-900 whitespace-nowrap" style={{ background: zemin(c) }}>
                        {c === 0 ? <span className="text-ink-400">-</span> : fmtTRY(c)}
                      </td>
                    );
                  })}
                  {m.subeler.length > 1 && <td className="py-2 pl-3 text-right tnum font-medium text-ink-900 whitespace-nowrap">{fmtTRY(k.toplam)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
